import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PatientSummary } from '@/api/patients/patientsApi.types';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { PatientFormModal } from '@/components/practitioner/PatientForm/PatientFormModal';
import { ADMIN_LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useWorkspacePatientsQuery } from '@/hooks/usePatientsQueries';
import { formatPatientAge } from '@/utils/patientAge';

export function PatientsPage() {
  const { t } = useTranslation('patients');
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [page, setPage] = useState(1);
  const [isAddPatientOpen, setIsAddPatientOpen] = useState(false);

  useEffect(
    function resetPageOnSearch() {
      setPage(1);
    },
    [debouncedSearch]
  );

  const patientsQuery = useWorkspacePatientsQuery(page, debouncedSearch);
  const result = patientsQuery.data;
  const hasSearch = debouncedSearch.trim().length > 0;

  return (
    <div className="p-6 md:p-8">
      <AdminPageHeader
        title={t('list.title')}
        subtitle={t('list.subtitle')}
        action={
          <Button
            type="button"
            onClick={function openAddPatient() {
              setIsAddPatientOpen(true);
            }}
          >
            <Icon name="Plus" /> {t('list.add')}
          </Button>
        }
      />

      <div className="mb-5">
        <ListSearchInput value={searchInput} onChange={setSearchInput} placeholder={t('list.search')} />
      </div>

      {patientsQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : patientsQuery.isError === true || result === undefined ? (
        <QueryErrorState
          onRetry={function retry() {
            patientsQuery.refetch();
          }}
        />
      ) : result.items.length === 0 ? (
        <Card padding="lg" className="text-center text-sm text-zoomuted">
          {hasSearch === true ? t('list.noResults') : t('list.empty')}
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-3">
            {result.items.map(function renderPatient(patient) {
              return (
                <PatientRow
                  key={patient.id}
                  patient={patient}
                  onOpen={function open() {
                    navigate('/workspace/patients/' + patient.id);
                  }}
                />
              );
            })}
          </div>
          <ListPagination
            page={page}
            pageSize={ADMIN_LIST_PAGE_SIZE}
            totalItems={result.totalItems}
            onPageChange={setPage}
          />
        </>
      )}

      {isAddPatientOpen === true ? (
        <PatientFormModal
          clientId={null}
          patient={null}
          onClose={function closeAddPatient() {
            setIsAddPatientOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

interface PatientRowProps {
  patient: PatientSummary;
  onOpen: () => void;
}

function PatientRow({ patient, onOpen }: PatientRowProps) {
  const { t } = useTranslation(['patients', 'common']);
  const ageLabel = formatPatientAge(patient.dateOfBirth, t);
  const meta = [patient.species, patient.breed]
    .filter(function keep(part) {
      return part !== null && part.trim().length > 0;
    })
    .join(' · ');

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left px-4 sm:px-5 py-4 flex items-center gap-3 bg-zoowhite border border-zooborder rounded-2xl shadow-sm hover:bg-zoovpalelavender hover:bg-opacity-30 transition-colors"
    >
      <span className="w-10 h-10 rounded-xl bg-zoovpalelavender flex items-center justify-center overflow-hidden shrink-0">
        {patient.photoUrl === null ? (
          <Icon name="PawPrint" className="text-zooprimary" />
        ) : (
          <img src={patient.photoUrl} alt={patient.name} className="w-full h-full object-cover" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-zooindigo truncate">{patient.name}</div>
        <div className="text-xs text-zoomuted truncate">
          {meta}
          {ageLabel === null ? '' : ' · ' + ageLabel}
        </div>
      </div>
      {patient.clientId === null ? (
        <Badge tone="muted" className="shrink-0">
          {t('patients:owner.noneBadge')}
        </Badge>
      ) : null}
      {patient.warnings.length === 0 ? null : (
        <Badge tone="warning" className="inline-flex items-center gap-1 shrink-0">
          <Icon name="AlertCircle" />
          <span className="hidden sm:inline">{patient.warnings.length}</span>
        </Badge>
      )}
      <Icon name="ChevronRight" className="text-zoomuted shrink-0" />
    </button>
  );
}
